/**
 * AddressAutocomplete
 *
 * Full-address input block used when the admin sets the guest-estimate form to
 * "autocomplete" mode. A typeahead (backed by the server proxy /api/address-
 * suggest.php) fills the structured fields on select; every field stays
 * editable. The provider + API key are server-side — this component is
 * provider-agnostic.
 *
 * Fields map to GuestEstimateFormData: addressOne, addressTwo, city, region,
 * postalCode, country, territory.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Loader2, AlertTriangle } from 'lucide-react';
import { fetchAddressSuggestionsWithProvider, fetchAddressDetail, AddressSuggestion, AddressDetail } from '../../services/api';
import { GuestEstimateFormData } from '../../types';

interface Props {
  values: Pick<GuestEstimateFormData, 'addressOne' | 'addressTwo' | 'city' | 'region' | 'postalCode' | 'country'>;
  errors: Partial<Record<keyof GuestEstimateFormData, string>>;
  regionLabel: string;
  onChange: (field: keyof GuestEstimateFormData, value: string) => void;
  /** Apply a resolved address as a single merge (avoids per-field churn). */
  onResolve: (detail: AddressDetail) => void;
  /** When true, show a non-blocking hint if a picked address lacks a street #. */
  nudgeIncomplete?: boolean;
}

export const AddressAutocomplete: React.FC<Props> = ({ values, errors, regionLabel, onChange, onResolve, nudgeIncomplete }) => {
  const { t } = useTranslation('results');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // Set when a picked address resolved without a street/building number.
  const [incomplete, setIncomplete] = useState(false);
  // Which geocoding provider answered the last search: routes the details call and
  // decides whether LocationIQ's mandatory attribution line is shown.
  const [provider, setProvider] = useState('');
  const skipNextRef = useRef(false); // skip the fetch caused by a programmatic fill
  const didMountRef = useRef(false); // skip the fetch on the very first render
  const boxRef = useRef<HTMLDivElement>(null);
  // One billing session per address search (Google Places): the same token is
  // reused across this search's suggestion calls + the detail lookup, then
  // cleared so the next search starts a fresh session.
  const sessionRef = useRef<string | null>(null);
  const ensureSession = (): string => {
    if (!sessionRef.current) {
      sessionRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    }
    return sessionRef.current;
  };

  // Debounced suggestions as the user types in the Address One search field.
  useEffect(() => {
    // Never fetch on mount. A value restored from a previous fill (local
    // storage) rehydrates addressOne with ≥3 chars, which is indistinguishable
    // from typing to this effect — firing here would open the dropdown and burn
    // a paid Places call before the user touches the field. Only subsequent
    // changes (actual typing) should search.
    if (!didMountRef.current) { didMountRef.current = true; return; }
    if (skipNextRef.current) { skipNextRef.current = false; return; }
    const q = values.addressOne.trim();
    if (q.length < 3) { setSuggestions([]); setOpen(false); return; }
    let cancelled = false;
    setLoading(true);
    const t = window.setTimeout(async () => {
      const res = await fetchAddressSuggestionsWithProvider(q, ensureSession());
      if (cancelled) return;
      setProvider(res.provider);
      setSuggestions(res.suggestions);
      setOpen(res.suggestions.length > 0);
      setLoading(false);
    }, 300);
    return () => { cancelled = true; window.clearTimeout(t); setLoading(false); };
  }, [values.addressOne]);

  // Close the dropdown on outside click.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = async (s: AddressSuggestion) => {
    setOpen(false);
    setSuggestions([]);
    skipNextRef.current = true; // the resolve below sets addressOne — don't re-search
    // Most providers send components with the suggestion, so the pick is free. Google
    // does not: its details lookup closes the session token (same token as the
    // suggestions) so the whole search bills as one session; reset for the next one.
    const detail = s.detail ?? await fetchAddressDetail(s.id, sessionRef.current ?? undefined, provider || undefined);
    sessionRef.current = null;
    if (detail) {
      onResolve(detail);
      // Flag a street-level / partial pick (e.g. "Ontario Street") so the
      // optional nudge can surface. Manual edits below clear it.
      setIncomplete(detail.hasStreetNumber === false);
    } else {
      // Lookup failed — keep the label as the street line at least.
      onChange('addressOne', s.label);
    }
  };

  return (
    <>
      {/* Address One — the typeahead search field */}
      <div className="row mb-3">
        <div className="col-12" ref={boxRef}>
          <label htmlFor="gem-addr1" className="form-label fw-semibold">
            {t('lead.fields.address')} <span className="text-danger">*</span>
          </label>
          {/* position-relative wrapper hugs the input exactly, so the absolute
              dropdown (start-0/end-0) can't overflow the modal like w-100 did. */}
          <div className="position-relative">
            <span className="position-absolute top-50 translate-middle-y ps-2 text-muted" style={{ left: 0, zIndex: 3 }}>
              <MapPin size={16} />
            </span>
            <input
              type="text"
              id="gem-addr1"
              name="gem-address-search"
              className={`form-control ps-4 ${errors.addressOne ? 'is-invalid' : ''}`}
              value={values.addressOne}
              onChange={e => { setIncomplete(false); onChange('addressOne', e.target.value); }}
              onFocus={() => { if (suggestions.length) setOpen(true); }}
              placeholder={t('lead.placeholders.address')}
              // Browsers ignore autocomplete="off" on address-looking fields, but
              // they DO honor a concrete non-address token — this overrides the
              // label/placeholder heuristics and kills Chrome's address bubble
              // without any password-manager UI (unlike "new-password").
              autoComplete="one-time-code"
              // Tell common password managers to keep out of this field too.
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
            />
            {loading && (
              <span className="position-absolute top-50 translate-middle-y end-0 pe-2 text-muted" style={{ zIndex: 3 }}>
                <Loader2 size={16} className="animate-spin" />
              </span>
            )}

            {open && suggestions.length > 0 && (
              <ul
                className="list-group position-absolute start-0 end-0 border rounded-3 shadow overflow-hidden bg-white"
                style={{ top: 'calc(100% + 4px)', zIndex: 1056, maxHeight: 260, overflowY: 'auto' }}
              >
                {suggestions.map(s => (
                  <li key={s.id}>
                    <button
                      type="button"
                      className="list-group-item list-group-item-action border-0 border-bottom small text-start w-100 d-flex align-items-start gap-2 py-2"
                      style={{ whiteSpace: 'normal' }}
                      onClick={() => pick(s)}
                    >
                      <MapPin size={14} className="text-muted flex-shrink-0 mt-1" />
                      <span className="text-break">{s.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {errors.addressOne && <div className="invalid-feedback d-block">{errors.addressOne}</div>}
          {nudgeIncomplete && incomplete && !errors.addressOne && (
            <div className="small text-warning-emphasis mt-1 d-flex align-items-center gap-1">
              <AlertTriangle size={13} className="flex-shrink-0" />
              {t('lead.addressNudge')}
            </div>
          )}
          {/* LocationIQ's free tier requires a visible attribution link on pages using it. */}
          {provider === 'locationiq' && (
            <div className="small text-muted mt-1">
              <a href="https://locationiq.com" target="_blank" rel="noopener noreferrer" className="text-muted">
                {t('lead.attribution.locationiq')}
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Address Two (optional) */}
      <div className="row mb-3">
        <div className="col-12">
          <label htmlFor="gem-addr2" className="form-label fw-semibold">
            {t('lead.fields.addressTwo')} <span className="text-muted small">{t('comments.optional')}</span>
          </label>
          <input
            type="text"
            id="gem-addr2"
            className="form-control"
            value={values.addressTwo}
            onChange={e => onChange('addressTwo', e.target.value)}
            placeholder={t('lead.placeholders.addressTwo')}
          />
        </div>
      </div>

      {/* City / Region */}
      <div className="row mb-3">
        <div className="col-12 col-sm-6 mb-3 mb-sm-0">
          <label htmlFor="gem-city2" className="form-label fw-semibold">
            {t('account:info.city')} <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            id="gem-city2"
            className={`form-control ${errors.city ? 'is-invalid' : ''}`}
            value={values.city}
            onChange={e => onChange('city', e.target.value)}
          />
          {errors.city && <div className="invalid-feedback">{errors.city}</div>}
        </div>
        <div className="col-12 col-sm-6">
          <label htmlFor="gem-region2" className="form-label fw-semibold">
            {regionLabel} <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            id="gem-region2"
            className={`form-control ${errors.region ? 'is-invalid' : ''}`}
            value={values.region}
            onChange={e => onChange('region', e.target.value)}
          />
          {errors.region && <div className="invalid-feedback">{errors.region}</div>}
        </div>
      </div>

      {/* Postal / Country */}
      <div className="row mb-3">
        <div className="col-12 col-sm-6 mb-3 mb-sm-0">
          <label htmlFor="gem-postal2" className="form-label fw-semibold">
            {t('account:info.postalCode')} <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            id="gem-postal2"
            className={`form-control ${errors.postalCode ? 'is-invalid' : ''}`}
            value={values.postalCode}
            onChange={e => onChange('postalCode', e.target.value)}
          />
          {errors.postalCode && <div className="invalid-feedback">{errors.postalCode}</div>}
        </div>
        <div className="col-12 col-sm-6">
          <label htmlFor="gem-country2" className="form-label fw-semibold">
            {t('account:info.country')} <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            id="gem-country2"
            className={`form-control ${errors.country ? 'is-invalid' : ''}`}
            value={values.country}
            onChange={e => onChange('country', e.target.value)}
          />
          {errors.country && <div className="invalid-feedback">{errors.country}</div>}
        </div>
      </div>
    </>
  );
};

export default AddressAutocomplete;
